import {datadogRum} from '@datadog/browser-rum';

import {envIsDev} from '../../../config';
import {recordDatadogLogInfo} from '../datadogLogs';
import {recordRumDurationVital} from '../datadogRum';
import {
  getInteractionMetrics,
  getMedian,
  isEventTimingEntry,
  isLongAnimationFrameEntry,
  type RecordedEventTiming,
} from './performanceMetrics';
import type {
  CriticalWorkflowAbandonedDatadogContext,
  CriticalWorkflowCompletedDatadogContext,
  CriticalWorkflowDurationVitalOptions,
  CriticalWorkflowInitiatingEvent,
  CriticalWorkflowObservationOptions,
} from './types';

export const CRITICAL_WORKFLOW_RESPONSIVENESS_VITAL_NAME =
  'critical_workflow_responsiveness';

// Developers can flip this manually while tuning an observed workflow.
const shouldLogToConsole = envIsDev && false;

interface ActiveObservation {
  id: number;
  workflow: string;
  windowDurationMs: number;
  startTime: number;
  endTime: number;
  startMarkName: string;
  initiatingEvent?: CriticalWorkflowInitiatingEvent;
  markNames: Set<string>;
  markDurationsMs: Map<string, number>;
  markCounts: Map<string, number>;
  longAnimationFrameDurationsMs: number[];
  totalBlockingDurationMs: number;
  timeoutId: number;
}

type RecordDurationVital = (
  name: typeof CRITICAL_WORKFLOW_RESPONSIVENESS_VITAL_NAME,
  options: CriticalWorkflowDurationVitalOptions
) => void;
type ReportProfilerUnavailable = (
  missingCapabilities: readonly string[]
) => void;

const OBSERVED_ENTRY_TYPES = ['long-animation-frame', 'mark'];
const REQUIRED_OBSERVER_ENTRY_TYPES = [
  'event',
  ...OBSERVED_ENTRY_TYPES,
] as const;
const EVENT_TIMING_DURATION_THRESHOLD_MS = 16;
const MAX_RECENT_EVENT_TIMINGS = 200;
const EVENT_TIMING_OBSERVER_OPTIONS: PerformanceObserverInit & {
  durationThreshold: number;
} = {
  type: 'event',
  buffered: true,
  durationThreshold: EVENT_TIMING_DURATION_THRESHOLD_MS,
};

const recordAbandonedDurationVital: RecordDurationVital = (name, options) => {
  // eslint-disable-next-line wandb/dd-vital-use-wrapper -- Page-lifecycle abandonment is reported after the view is hidden, so this experimental observer must bypass the hidden-view facade.
  datadogRum.addDurationVital(name, options);
};

const defaultRecordDurationVital: RecordDurationVital = (name, options) => {
  if (options.context.outcome === 'abandoned') {
    recordAbandonedDurationVital(name, options);
    return;
  }
  recordRumDurationVital(name, options);
};

const defaultReportProfilerUnavailable: ReportProfilerUnavailable =
  missingCapabilities => {
    recordDatadogLogInfo(
      'critical_workflow_responsiveness_profiler_unavailable',
      {missingCapabilities}
    );
  };

export class CriticalWorkflowResponsivenessMonitor {
  private observer: PerformanceObserver | undefined;
  private eventTimingObserver: PerformanceObserver | undefined;
  private eventTimingSubscriberCount = 0;
  private readonly recentEventTimings: RecordedEventTiming[] = [];
  private readonly activeObservations = new Map<number, ActiveObservation>();
  private nextObservationId = 0;
  private listeningForAbandonment = false;
  private profilerSupported: boolean | undefined;

  constructor(
    private readonly recordDurationVital: RecordDurationVital = defaultRecordDurationVital,
    private readonly reportProfilerUnavailable: ReportProfilerUnavailable = defaultReportProfilerUnavailable
  ) {}

  initialize(): () => void {
    if (!this.ensureSupported()) {
      return () => {};
    }

    this.eventTimingSubscriberCount += 1;
    if (this.eventTimingObserver == null) {
      this.eventTimingObserver = new PerformanceObserver(list => {
        this.recordEventTimings(list.getEntries());
      });
      this.eventTimingObserver.observe(EVENT_TIMING_OBSERVER_OPTIONS);
    }

    let isStopped = false;
    return () => {
      if (isStopped) {
        return;
      }
      isStopped = true;
      this.eventTimingSubscriberCount -= 1;
      if (this.eventTimingSubscriberCount === 0) {
        this.eventTimingObserver?.disconnect();
        this.eventTimingObserver = undefined;
        this.recentEventTimings.length = 0;
      }
    };
  }

  observe(options: CriticalWorkflowObservationOptions): boolean {
    if (!this.ensureSupported() || !this.isValidOptions(options)) {
      return false;
    }

    this.connect();

    const id = this.nextObservationId++;
    const startMarkName = `${options.workflow}-start`;
    const startTime = performance.mark(startMarkName).startTime;
    const markNames = new Set(options.markNames);
    markNames.add(startMarkName);
    const markCounts = new Map<string, number>();
    for (const markName of markNames) {
      markCounts.set(markName, 0);
    }
    const observation: ActiveObservation = {
      id,
      workflow: options.workflow,
      windowDurationMs: options.windowDurationMs,
      startTime,
      endTime: startTime + options.windowDurationMs,
      startMarkName,
      initiatingEvent: options.initiatingEvent,
      markNames,
      markDurationsMs: new Map(),
      markCounts,
      longAnimationFrameDurationsMs: [],
      totalBlockingDurationMs: 0,
      timeoutId: window.setTimeout(
        () => this.completeObservation(id),
        options.windowDurationMs
      ),
    };
    this.activeObservations.set(id, observation);

    return true;
  }

  private readonly handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this.abandonObservations();
    }
  };

  private readonly handlePageHide = () => {
    this.abandonObservations();
  };

  private ensureSupported(): boolean {
    if (this.profilerSupported != null) {
      return this.profilerSupported;
    }

    const missingCapabilities = this.getMissingCapabilities();
    if (missingCapabilities.length === 0) {
      this.profilerSupported = true;
      return true;
    }
    this.profilerSupported = false;
    this.reportProfilerUnavailable(missingCapabilities);
    return false;
  }

  private getMissingCapabilities(): string[] {
    const missingCapabilities: string[] = [];

    if (typeof window === 'undefined') {
      missingCapabilities.push('window');
    }
    if (typeof document === 'undefined') {
      missingCapabilities.push('document');
    }
    if (typeof performance === 'undefined') {
      missingCapabilities.push('performance');
    } else {
      if (typeof performance.mark !== 'function') {
        missingCapabilities.push('performance.mark');
      }
      if (typeof performance.now !== 'function') {
        missingCapabilities.push('performance.now');
      }
      if (!Number.isFinite(performance.timeOrigin)) {
        missingCapabilities.push('performance.timeOrigin');
      }
    }

    if (typeof PerformanceObserver === 'undefined') {
      missingCapabilities.push('PerformanceObserver');
    } else if (!Array.isArray(PerformanceObserver.supportedEntryTypes)) {
      missingCapabilities.push('PerformanceObserver.supportedEntryTypes');
    } else {
      for (const entryType of REQUIRED_OBSERVER_ENTRY_TYPES) {
        if (!PerformanceObserver.supportedEntryTypes.includes(entryType)) {
          missingCapabilities.push(`PerformanceObserver:${entryType}`);
        }
      }
    }

    return missingCapabilities;
  }

  private isValidOptions(options: CriticalWorkflowObservationOptions): boolean {
    return (
      options.workflow.length > 0 &&
      Number.isFinite(options.windowDurationMs) &&
      options.windowDurationMs > 0
    );
  }

  private connect(): void {
    if (this.observer != null) {
      return;
    }

    this.observer = new PerformanceObserver(list => {
      this.processEntries(list.getEntries());
    });
    this.observer.observe({entryTypes: OBSERVED_ENTRY_TYPES});

    if (!this.listeningForAbandonment) {
      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
      window.addEventListener('pagehide', this.handlePageHide);
      this.listeningForAbandonment = true;
    }
  }

  private processEntries(entries: PerformanceEntryList): void {
    for (const entry of entries) {
      for (const observation of this.activeObservations.values()) {
        if (
          entry.startTime < observation.startTime ||
          entry.startTime >= observation.endTime ||
          (entry.entryType === 'mark' &&
            entry.name === observation.startMarkName &&
            entry.startTime <= observation.startTime)
        ) {
          continue;
        }

        if (entry.entryType === 'mark') {
          this.processMark(entry, observation);
        } else if (isLongAnimationFrameEntry(entry)) {
          observation.longAnimationFrameDurationsMs.push(entry.duration);
          observation.totalBlockingDurationMs += entry.blockingDuration;
        }
      }
    }
  }

  private recordEventTimings(entries: PerformanceEntryList): void {
    for (const entry of entries) {
      if (!isEventTimingEntry(entry)) {
        continue;
      }
      this.recentEventTimings.push({
        name: entry.name,
        startTime: entry.startTime,
        duration: entry.duration,
        processingStart: entry.processingStart,
        processingEnd: entry.processingEnd,
        interactionId: entry.interactionId,
      });
    }

    if (this.recentEventTimings.length > MAX_RECENT_EVENT_TIMINGS) {
      this.recentEventTimings.splice(
        0,
        this.recentEventTimings.length - MAX_RECENT_EVENT_TIMINGS
      );
    }
  }

  private processMark(
    entry: PerformanceEntry,
    observation: ActiveObservation
  ): void {
    if (!observation.markNames.has(entry.name)) {
      return;
    }

    observation.markCounts.set(
      entry.name,
      (observation.markCounts.get(entry.name) ?? 0) + 1
    );
    if (!observation.markDurationsMs.has(entry.name)) {
      observation.markDurationsMs.set(
        entry.name,
        entry.startTime - observation.startTime
      );
    }
  }

  private completeObservation(id: number): void {
    this.drainPendingEntries();
    this.drainPendingEventTimingEntries();
    const observation = this.activeObservations.get(id);
    if (observation == null) {
      return;
    }

    const frameDurations = observation.longAnimationFrameDurationsMs;
    const medianFrameDuration = getMedian(frameDurations);
    const interactionMetrics = getInteractionMetrics(
      this.recentEventTimings,
      observation.initiatingEvent
    );
    const context: CriticalWorkflowCompletedDatadogContext = {
      workflow: observation.workflow,
      outcome: 'completed',
      configuredWindowDurationMs: observation.windowDurationMs,
      markDurationsMs: Object.fromEntries(observation.markDurationsMs),
      markCounts: Object.fromEntries(observation.markCounts),
      longAnimationFrameCount: frameDurations.length,
      totalBlockingDurationMs: observation.totalBlockingDurationMs,
      blockedPercentage:
        (observation.totalBlockingDurationMs / observation.windowDurationMs) *
        100,
      ...interactionMetrics,
    };

    if (frameDurations.length > 0) {
      context.maxLongAnimationFrameDurationMs = Math.max(...frameDurations);
      context.medianLongAnimationFrameDurationMs = medianFrameDuration;
    }

    this.finishObservation(observation, {
      startTime: performance.timeOrigin + observation.startTime,
      duration: observation.windowDurationMs,
      context,
    });
  }

  private abandonObservations(): void {
    if (this.activeObservations.size === 0) {
      return;
    }

    this.drainPendingEntries();
    const abandonTime = performance.now();
    for (const observation of this.activeObservations.values()) {
      const context: CriticalWorkflowAbandonedDatadogContext = {
        workflow: observation.workflow,
        outcome: 'abandoned',
        configuredWindowDurationMs: observation.windowDurationMs,
        markDurationsMs: Object.fromEntries(observation.markDurationsMs),
        markCounts: Object.fromEntries(observation.markCounts),
      };
      this.finishObservation(observation, {
        startTime: performance.timeOrigin + observation.startTime,
        duration: Math.min(
          observation.windowDurationMs,
          Math.max(0, abandonTime - observation.startTime)
        ),
        context,
      });
    }
  }

  private drainPendingEntries(): void {
    if (this.observer != null) {
      this.processEntries(this.observer.takeRecords());
    }
  }

  private drainPendingEventTimingEntries(): void {
    if (this.eventTimingObserver != null) {
      this.recordEventTimings(this.eventTimingObserver.takeRecords());
    }
  }

  private finishObservation(
    observation: ActiveObservation,
    vital: CriticalWorkflowDurationVitalOptions
  ): void {
    window.clearTimeout(observation.timeoutId);
    this.activeObservations.delete(observation.id);
    this.disconnectIfIdle();
    if (shouldLogToConsole) {
      console.info(CRITICAL_WORKFLOW_RESPONSIVENESS_VITAL_NAME, vital);
    }
    this.recordDurationVital(
      CRITICAL_WORKFLOW_RESPONSIVENESS_VITAL_NAME,
      vital
    );
  }

  private disconnectIfIdle(): void {
    if (this.activeObservations.size > 0) {
      return;
    }

    this.observer?.disconnect();
    this.observer = undefined;

    if (this.listeningForAbandonment) {
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
      window.removeEventListener('pagehide', this.handlePageHide);
      this.listeningForAbandonment = false;
    }
  }
}

const criticalWorkflowResponsivenessMonitor =
  new CriticalWorkflowResponsivenessMonitor();

export const initializeCriticalWorkflowResponsivenessMonitoring =
  (): (() => void) => criticalWorkflowResponsivenessMonitor.initialize();

export const observeCriticalWorkflowResponsiveness = (
  options: CriticalWorkflowObservationOptions
): boolean => criticalWorkflowResponsivenessMonitor.observe(options);
